import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BarChartComponent } from './structure/bar-chart.component';
import { DoughnutChartComponent } from './structure/doughnut-chart/doughnut-chart.component';
import { GaugeChartComponent } from './structure/gauge-chart/gauge-chart.component';
import { GroupedBarChartComponent } from './structure/bar-chart/grouped-bar-chart.component';
import { OptionsComponent } from './structure/options/options.component';
import { LegendComponent } from './structure/legend/legend.component';
import { TotalComponent } from './structure/total/total.component';
import { CropType, DashboardTab, ensureCropData, GaugeChartModel } from '../../models/crop.interface';
import { DashboardDataService } from '../../services/dashboard-data.service';
import { ModalService } from '../../services/modal.service';
import {
  ExportCommandsDialogContext,
  ExportCommandsDialogComponent,
} from '../exports/export-commands-dialog.component';
import { ExportsService } from '../../services/exports.service';
import { saveAs } from 'file-saver';
import { ExportConfirmDialogContext, ExportConfirmDialogComponent } from '../exports/export-confirm-modal.component';
import { ARBleService } from '../../services/agrorendement-ble.service';
import { ARColzaService } from '../../services/agrorendement-colza.service';
import { AROrgeService } from '../../services/agrorendement-orge.service';
import { Campaign } from '../../models/campaign.model';
import { ARCommonListsService } from '../../services/agrorendement-common-lists.service';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { DASH_COLORS, GAUGE_SEGMENTS } from '../../utils/dashboard.utils';

type SpecyKey = 'ble' | 'colza' | 'orge';
interface LastUpdatedBySpecy {
  ble?: Date;
  colza?: Date;
  orge?: Date;
}
interface CampaignBySpecy {
  ble?: number;
  colza?: number;
  orge?: number;
}

@Component({
  standalone: true,
  selector: 'chart-dashboard',
  styleUrls: ['chart-dashboard.component.scss'],
  templateUrl: './chart-dashboard.component.html',
  imports: [
    CommonModule,
    BarChartComponent,
    DoughnutChartComponent,
    GaugeChartComponent,
    GroupedBarChartComponent,
    OptionsComponent,
    LegendComponent,
    TotalComponent,
  ],
  providers: [DashboardDataService, ExportsService, ARCommonListsService, ARBleService, ARColzaService, AROrgeService],
})
export class ChartDashboard implements OnInit, OnDestroy {
  tabs: DashboardTab[];
  currentTab: DashboardTab;
  showOptions: boolean = true;

  campaignBySpecy: CampaignBySpecy = { ble: undefined, colza: undefined, orge: undefined };

  isRefreshing: boolean = false;
  cooldownSeconds: number = 30;

  private nextAllowedAt: number = 0;
  private lastUpdatedBySpecy: LastUpdatedBySpecy = {};
  cooldownRemaining: number = 0;
  private cooldownTimer: any = null;

  private pendingRequests = 0;
  private _cycleServedAtMs: number = 0;
  private _cycleIsManual: boolean = false;
  private _cycleSpecies: SpecyKey[] = [];

  constructor(
    private dashboardDataService: DashboardDataService,
    private modal: ModalService,
    private exportsService: ExportsService,
    private bleService: ARBleService,
    private colzaService: ARColzaService,
    private orgeService: AROrgeService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.tabs = this.dashboardDataService.getTabs();
    this.currentTab = this.tabs[0];

    this.restoreCooldownState();
    this.restoreLastUpdatedState();

    this.selectTab(this.currentTab);

    this.bleService.getCampaigns().subscribe((cs: any) => {
      const latest = cs.sort(Campaign.compare).reverse()[0];
      if (latest) this.campaignBySpecy.ble = latest.id;
    });
    this.colzaService.getCampaigns().subscribe((cs: any) => {
      const latest = cs.sort(Campaign.compare).reverse()[0];
      if (latest) this.campaignBySpecy.colza = latest.id;
    });
    this.orgeService.getCampaigns().subscribe((cs: any) => {
      const latest = cs.sort(Campaign.compare).reverse()[0];
      if (latest) this.campaignBySpecy.orge = latest.id;
    });

    this.startCooldownTimerIfNeeded();
    this.updateCooldownRemaining();
  }

  ngOnDestroy(): void {
    this.stopCooldownTimer();
  }

  canRefresh(): boolean {
    return !this.isRefreshing && Date.now() >= this.nextAllowedAt;
  }

  onManualRefresh(): void {
    if (!this.canRefresh()) return;
    this.isRefreshing = true;
    this._cycleIsManual = true;
    this._cycleServedAtMs = 0;
    this._cycleSpecies = this.tabs.map((tab) => this.cropToSpecy(tab.type));
    this.fetchAllCrops(true);
  }

  private selectCooldownOriginIfNeeded(): void {
    if (!this._cycleIsManual) return;
    this.nextAllowedAt = Date.now() + this.cooldownSeconds * 1000;
    this.persistCooldownState();
  }

  private markRefreshed(): void {
    this.isRefreshing = false;
    if (this._cycleServedAtMs) {
      const servedAt = new Date(this._cycleServedAtMs);
      this._cycleSpecies.forEach((specy) => (this.lastUpdatedBySpecy[specy] = servedAt));
      this.persistLastUpdatedState();
    }
    this.selectCooldownOriginIfNeeded();
    this._cycleIsManual = false;
    this._cycleSpecies = [];
    this.updateCooldownRemaining();
    this.startCooldownTimerIfNeeded();
  }

  private updateCooldownRemaining(): void {
    const msLeft = Math.max(0, this.nextAllowedAt - Date.now());
    this.cooldownRemaining = Math.ceil(msLeft / 1000);
    if (this.cooldownRemaining <= 0) this.stopCooldownTimer();
  }

  private startCooldownTimerIfNeeded(): void {
    if (this.cooldownTimer) return;
    this.cooldownTimer = setInterval(() => this.updateCooldownRemaining(), 1000);
  }

  private stopCooldownTimer(): void {
    if (!this.cooldownTimer) return;
    clearInterval(this.cooldownTimer);
    this.cooldownTimer = null;
  }

  selectTab(tab: DashboardTab): void {
    this.currentTab = tab;
    this.updateCooldownRemaining();
    // A refresh in flight already covers every crop, so don't kick off a partial load on top of it.
    if (this.isRefreshing) return;
    if (!tab.data || !tab.data.commandes || !tab.data.commandes.total || !tab.data.commandes.total.barChart) {
      this._cycleIsManual = false;
      this._cycleServedAtMs = 0;
      this._cycleSpecies = [this.cropToSpecy(tab.type)];
      this.fetchDataForTab(tab.type, false);
    }
  }

  private commandDoughnutKeys(dnObj: any): string[] {
    return dnObj ? Object.keys(dnObj) : [];
  }

  private getDoughnutOrder(cropType: CropType): string[] {
    switch (cropType) {
      case CropType.BLE:
        return ['BT1', 'BT2', 'B2N', 'BT3'];
      case CropType.COLZA:
        return [];
      case CropType.ORGE:
        return ['OT1', 'OT2', 'OT3'];
      default:
        return [];
    }
  }

  private sortDoughnutKeys(doughnutMap: any, order: string[]): string[] {
    if (!doughnutMap) return [];
    if (order.length === 0) return [];
    return Object.keys(doughnutMap)
      .filter((key) => order.indexOf(key) !== -1)
      .sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  private buildSubDoughnut(
    subKey: string,
    stats: any,
  ): {
    chart: any;
    completeArea: number;
    incompleteArea: number;
    completeCount: number;
    incompleteCount: number;
  } {
    const completeArea = stats.completeArea || 0;
    const incompleteArea = stats.incompleteArea || 0;
    const completeCount = stats.completeCount || 0;
    const incompleteCount = stats.incompleteCount || 0;
    const chart = {
      labels: ['Complétés', 'Incomplets'],
      datasets: [{ data: [completeCount, incompleteCount], backgroundColor: ['#bad424', '#E15759'] }],
      title: this.getTitleLabel(subKey),
      infoComplete: 'Complétés: ' + completeCount + ' parcelles / ' + completeArea + ' ha',
      infoIncomplete: 'Incomplets: ' + incompleteCount + ' parcelles / ' + incompleteArea + ' ha',
    };
    return { chart, completeArea, incompleteArea, completeCount, incompleteCount };
  }

  private buildBarChartData(
    fieldsLastYear: number,
    fieldsThisYear: number,
    labelLastYear: string,
    labelThisYear: string,
  ) {
    const colorThisYear = fieldsThisYear >= fieldsLastYear ? '#bad424' : '#E15759';
    return {
      labels: ['Commandes livrées N-1', 'Commandes N'],
      datasets: [
        {
          label: labelLastYear,
          data: [fieldsLastYear, 0],
          backgroundColor: '#4E79A7',
          stack: 'centrage',
          maxBarThickness: 100
        },
        {
          label: labelThisYear,
          data: [0, fieldsThisYear],
          backgroundColor: colorThisYear,
          stack: 'centrage',
          maxBarThickness: 100
        },
      ],
    };
  }

  private buildAverageDoughnutChart(stats: any): any {
    const completeArea = stats.completeArea || 0;
    const incompleteArea = stats.incompleteArea || 0;
    const completeCount = stats.completeCount || 0;
    const incompleteCount = stats.incompleteCount || 0;
    return {
      labels: ['Complétés', 'Incomplets'],
      datasets: [{ data: [completeCount, incompleteCount], backgroundColor: ['#bad424', '#E15759'] }],
      title: 'Total',
      infoComplete: 'Complétés: ' + completeCount + ' parcelles / ' + completeArea.toFixed(2) + ' ha',
      infoIncomplete: 'Incomplets: ' + incompleteCount + ' parcelles / ' + incompleteArea.toFixed(2) + ' ha',
    };
  }

  private buildSubDoughnutsOnly(doughnutMap: any, cropType: CropType): any {
    const order = this.getDoughnutOrder(cropType);
    const sortedKeys = this.sortDoughnutKeys(doughnutMap, order);
    const subDoughnuts: any = {};
    for (let i = 0; i < sortedKeys.length; i++) {
      const subKey = sortedKeys[i];
      const stats = doughnutMap[subKey];
      const built = this.buildSubDoughnut(subKey, stats);
      subDoughnuts[subKey] = built.chart;
    }
    return subDoughnuts;
  }

  private bumpPending(): void {
    this.pendingRequests++;
  }
  private dropPendingAndMaybeFinish(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    if (this.pendingRequests === 0) this.markRefreshed();
  }

  /**
   * Refreshes all three crops through the aggregated endpoint: one request instead of the fifteen
   * the fan-out used to send. The server computes the fifteen charts in parallel, so we don't pay
   * the price of chaining them.
   */
  private fetchAllCrops(force: boolean): void {
    this.bumpPending();
    this.dashboardDataService
      .getAllDashboards(force)
      .pipe(finalize(() => this.dropPendingAndMaybeFinish()))
      .subscribe((res: any) => {
        this.bumpServedAtFrom(res && res.meta);
        const payload = (res && res.data) || {};
        this.tabs.forEach((tab) => {
          const charts = payload[this.cropToSpecy(tab.type)];
          if (!charts) return;
          this.applyBarChart(tab.type, charts.bar);
          this.applyDoughnut(tab.type, charts.doughnut);
          this.applyDoughnutAverage(tab.type, charts.doughnutAverage);
          this.applyGauge(tab.type, charts.gauge);
          this.applyGroupedBar(tab.type, charts.groupedBar);
        });
      });
  }

  private fetchDataForTab(cropType: CropType, force: boolean): void {
    const targetTab = this.tabs.find((t) => t.type === cropType);
    if (!targetTab) return;

    this.bumpPending();
    this.dashboardDataService
      .getBarChart(cropType, force)
      .pipe(finalize(() => this.dropPendingAndMaybeFinish()))
      .subscribe((res: any) => {
        this.bumpServedAtFrom(res && res.meta);
        this.applyBarChart(cropType, res && res.data);
      });

    this.bumpPending();
    this.dashboardDataService
      .getDoughnutData(cropType, force)
      .pipe(finalize(() => this.dropPendingAndMaybeFinish()))
      .subscribe((res: any) => {
        this.bumpServedAtFrom(res && res.meta);
        this.applyDoughnut(cropType, res && res.data);
      });

    this.bumpPending();
    this.dashboardDataService
      .getDoughnutAverage(cropType, force)
      .pipe(finalize(() => this.dropPendingAndMaybeFinish()))
      .subscribe((res: any) => {
        this.bumpServedAtFrom(res && res.meta);
        this.applyDoughnutAverage(cropType, res && res.data);
      });

    this.bumpPending();
    this.dashboardDataService
      .getGaugeData(cropType, force)
      .pipe(finalize(() => this.dropPendingAndMaybeFinish()))
      .subscribe((res: any) => {
        this.bumpServedAtFrom(res && res.meta);
        this.applyGauge(cropType, res && res.data);
      });

    this.bumpPending();
    this.dashboardDataService
      .getGroupedBarData(cropType, force)
      .pipe(finalize(() => this.dropPendingAndMaybeFinish()))
      .subscribe((res: any) => {
        this.bumpServedAtFrom(res && res.meta);
        this.applyGroupedBar(cropType, res && res.data);
      });
  }

  // --- Payload-to-model mapping. Shared by the aggregated endpoint and the per-crop calls. ---

  private cropDataFor(cropType: CropType): any {
    const tab = this.tabs.find((t) => t.type === cropType);
    return tab ? ensureCropData(tab) : null;
  }

  private applyBarChart(cropType: CropType, data: any): void {
    const cropData = this.cropDataFor(cropType);
    if (!cropData) return;
    const d = data || {};
    const fieldsLast = d.delivered_fields_last_year || 0;
    const fieldsN = d.count_parcels_n || 0;
    const areaLast = d.delivered_area_last_year || 0;
    const areaN = d.area_parcels_n || 0;
    const labelLastYear = areaLast + ' ha / ' + fieldsLast + ' parcelles';
    const labelThisYear = areaN + ' ha / ' + fieldsN + ' parcelles';
    cropData.commandes.total.barChart = this.buildBarChartData(fieldsLast, fieldsN, labelLastYear, labelThisYear);
  }

  private applyDoughnut(cropType: CropType, data: any): void {
    const cropData = this.cropDataFor(cropType);
    if (!cropData) return;
    cropData.commandes.doughnutCharts = this.buildSubDoughnutsOnly(data || {}, cropType);
  }

  private applyDoughnutAverage(cropType: CropType, data: any): void {
    const cropData = this.cropDataFor(cropType);
    if (!cropData) return;
    const d = data || {};
    const totalKey = Object.keys(d)[0];
    cropData.commandes.total.doughnutChart = this.buildAverageDoughnutChart(d[totalKey] || {});
  }

  private applyGauge(cropType: CropType, data: any): void {
    const cropData = this.cropDataFor(cropType);
    if (!cropData) return;
    const gaugeArray: GaugeChartModel[] = [];
    const gaugeData = data || {};
    Object.keys(gaugeData).forEach((subKey) => {
      const s = gaugeData[subKey] || {};
      const desc = [
        {
          color: DASH_COLORS.livre,
          text: `Livrés : ${s.deliveredArea || 0} ha / ${s.deliveredCount || 0} parcelles`,
        },
        {
          color: DASH_COLORS.restalivrer,
          text: `Reste à livrer : ${s.remainingArea || 0} ha / ${s.remainingCount || 0} parcelles   Planifiés à 7 jours : ${s.nextWeekArea || 0} ha / ${s.nextWeekCount || 0} parcelles`,
        },
        {
          color: DASH_COLORS.retard,
          text: `Retard : ${s.lateArea || 0} ha / ${s.lateCount || 0} parcelles   Retard moyen : ${(s.averageDelayDays || 0).toFixed(1)} jours`,
        },
      ];
      const total = (s.deliveredCount || 0) + (s.remainingCount || 0) + (s.lateCount || 0);
      const pctDelivered = total > 0 ? Math.round(((s.deliveredCount || 0) / total) * 100) : 0;
      const descPos: any = 'left';
      gaugeArray.push({
        title: subKey,
        progressPercentage: pctDelivered,
        colorSegments: GAUGE_SEGMENTS,
        description: desc,
        descriptionPosition: descPos,
        bottomLeftLabel: 'Non livrés',
        bottomRightLabel: 'Livrés',
      });
    });
    cropData.conseils.gaugeCharts = gaugeArray;
  }

  private applyGroupedBar(cropType: CropType, data: any): void {
    const cropData = this.cropDataFor(cropType);
    if (!cropData) return;
    const payload = data || {};
    const out: any = {};
    Object.keys(payload).forEach((subKey: string) => {
      const m = payload[subKey] || {};
      if (cropType === CropType.BLE && ['B2N', 'BT3'].indexOf(subKey) === -1) return;
      if (cropType === CropType.COLZA && subKey !== 'SH') return;
      if (cropType === CropType.ORGE && subKey !== 'OT3') return;
      let metrics: any[] = [];
      switch (cropType) {
        case CropType.BLE:
        case CropType.ORGE:
          metrics = [
            { name: 'Biomasse', unit: 't/ha', n1: m.biomassN1 || 0, n: m.biomassN || 0 },
            { name: 'Azote absorbé', unit: 'uN', n1: m.azoteAbsN1 || 0, n: m.azoteAbsN || 0 },
            { name: 'Azote préconisé', unit: 'uN', n1: m.azotePrecoN1 || 0, n: m.azotePrecoN || 0 },
          ];
          break;

        case CropType.COLZA:
          if (subKey === 'SH') {
            const mEH = payload['EH'] || {};
            metrics = [
              { name: 'Biomasse entrée hiver', unit: 'kg/m²', n1: mEH.biomassN1 || 0, n: mEH.biomassN || 0 },
              { name: 'Biomasse sortie hiver', unit: 'kg/m²', n1: m.biomassN1 || 0, n: m.biomassN || 0 },
              { name: 'Azote préconisé', unit: 'uN', n1: m.azotePrecoN1 || 0, n: m.azotePrecoN || 0 },
            ];
          }
          break;

        default:
          metrics = [];
          break;
      }

      out[subKey] = { metrics };
    });
    cropData.conseils.groupedBarCharts = out;
  }

  hasBarChartFor(key: string): boolean {
    const cb = this.currentTab.data && this.currentTab.data.conseils && this.currentTab.data.conseils.groupedBarCharts;
    return !!(cb && cb[key]);
  }

  getBarChartFor(key: string): any {
    return this.currentTab.data && this.currentTab.data.conseils
      ? this.currentTab.data.conseils.groupedBarCharts[key]
      : null;
  }

  getGroupedBarChartKeys(): string[] {
    const cb = this.currentTab.data && this.currentTab.data.conseils && this.currentTab.data.conseils.groupedBarCharts;
    return cb ? Object.keys(cb) : [];
  }

  getGroupedBarChartsWithoutGauge(): string[] {
    const allBarChartKeys = this.getGroupedBarChartKeys();
    const gaugeKeys =
      this.currentTab.data && this.currentTab.data.conseils && this.currentTab.data.conseils.gaugeCharts
        ? this.currentTab.data.conseils.gaugeCharts.map(function (g) {
            return g.title;
          })
        : [];
    return allBarChartKeys.filter(function (key) {
      return gaugeKeys.indexOf(key) === -1;
    });
  }

  getTitleLabel(key: string): string {
    const titles: { [k: string]: string } = {
      BT1: 'Tallage',
      BT2: 'Début montaison',
      B2N: '2 nœuds',
      BT3: 'Dernier apport',
      EH: 'Entrée hiver',
      SH: 'Sortie hiver',
      OT1: 'Tallage',
      OT2: 'Début montaison',
      OT3: 'Fin montaison',
    };
    return titles[key] || key;
  }

  private cropToSpecy(crop: CropType): SpecyKey {
    return crop === CropType.BLE ? 'ble' : crop === CropType.COLZA ? 'colza' : 'orge';
  }

  private currentSpecyKey(): SpecyKey {
    return this.cropToSpecy(this.currentTab.type);
  }

  private getCampaignIdForExport(specy: SpecyKey): number {
    if (specy === 'ble') return this.campaignBySpecy.ble || 0;
    if (specy === 'colza') return this.campaignBySpecy.colza || 0;
    return this.campaignBySpecy.orge || 0;
  }

  onExportIncompleteCommands() {
    const specy = this.currentSpecyKey();
    const campaignId = this.getCampaignIdForExport(specy);
    if (!campaignId) {
      console.error('No campaign id available yet for', specy);
      return;
    }
    const ctx = new ExportConfirmDialogContext();
    ctx.title = 'Export des commandes incomplètes';
    ctx.message = "Voulez-vous exporter les commandes incomplètes pour l'onglet actuel ?";
    this.modal.open(ExportConfirmDialogComponent, ctx).then((ref) => {
      if (ref) {
        this.exportsService.downloadIncompleteCommands(specy, campaignId).subscribe(
          (blob: any) => saveAs(blob, `Commandes_${specy}_incompletes.xlsx`),
          (err: any) => console.error('Export incomplete commandes failed', err),
        );
      }
    });
  }

  onViewIncompleteOrders(slice: 'complete' | 'incomplete') {
    const specy = this.currentSpecyKey();
    const campaignId = this.getCampaignIdForExport(specy);
    this.router.navigate([
      '/orders',
      'azote',
      specy,
      'parcels',
      {
        status: slice,
        onlyIncomplete: slice === 'incomplete' ? 1 : 0,
        campaignId: campaignId || null,
      },
    ]);
  }

  onExportLateConseils() {
    const specy = this.currentSpecyKey();
    const campaignId = this.getCampaignIdForExport(specy);
    if (!campaignId) {
      console.error('No campaign id available yet for', specy);
      return;
    }
    const ctx = new ExportConfirmDialogContext();
    ctx.title = 'Export des conseils en retard';
    ctx.message = "Voulez-vous exporter les conseils en retard pour l'onglet actuel ?";
    this.modal.open(ExportConfirmDialogComponent, ctx).then((ref) => {
      if (ref) {
        this.exportsService.downloadLateCommands(specy, campaignId).subscribe(
          (blob: any) => saveAs(blob, `Conseils_${specy}_en_retard.xlsx`),
          (err: any) => console.error('Export late conseils failed', err),
        );
      }
    });
  }

  onViewDeliveredConseils() {
    const specy = this.currentSpecyKey();
    this.router.navigate(['/results', 'azote', specy]);
  }

  get currentLastUpdated(): Date {
    return this.lastUpdatedBySpecy[this.currentSpecyKey()] || null;
  }

  private bumpServedAtFrom(meta: any): void {
    if (!meta || !meta.cache || !meta.cache.updatedAt) return;
    const t = Date.parse(meta.cache.updatedAt);
    if (!isNaN(t)) this._cycleServedAtMs = Math.max(this._cycleServedAtMs || 0, t);
  }

  private persistCooldownState(): void {
    try {
      sessionStorage.setItem('dash.cooldownUntil', JSON.stringify(this.nextAllowedAt));
    } catch (e) {}
  }

  private restoreCooldownState(): void {
    try {
      const raw = sessionStorage.getItem('dash.cooldownUntil');
      if (!raw) return;
      const gate = JSON.parse(raw);
      if (typeof gate === 'number' && isFinite(gate)) this.nextAllowedAt = gate;
    } catch (e) {}
  }

  private persistLastUpdatedState(): void {
    try {
      const obj: any = {};
      if (this.lastUpdatedBySpecy.ble) obj.ble = this.lastUpdatedBySpecy.ble.toISOString();
      if (this.lastUpdatedBySpecy.colza) obj.colza = this.lastUpdatedBySpecy.colza.toISOString();
      if (this.lastUpdatedBySpecy.orge) obj.orge = this.lastUpdatedBySpecy.orge.toISOString();
      sessionStorage.setItem('dash.lastUpdated', JSON.stringify(obj));
    } catch (e) {}
  }

  private restoreLastUpdatedState(): void {
    try {
      const raw = sessionStorage.getItem('dash.lastUpdated');
      if (!raw) return;
      const obj = JSON.parse(raw);
      if (obj && obj.ble) this.lastUpdatedBySpecy.ble = new Date(obj.ble);
      if (obj && obj.colza) this.lastUpdatedBySpecy.colza = new Date(obj.colza);
      if (obj && obj.orge) this.lastUpdatedBySpecy.orge = new Date(obj.orge);
    } catch (e) {}
  }
}
